import { useQuery, keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { marketIntegrationService } from '../lib/api/marketplaceIntegration';
import Swal from 'sweetalert2';
import { ErrorIcon, SuccessIcon } from '../lib/constants/messages';
import { claimedCandidateExternalService } from '../lib/api/claimed';
import { decryptData } from '../containers/claimed/search-box';
import { localStorageGetItem, localStorageSetItem } from '../components/storage/localStorageManager';
import { emailService } from '../lib/api/email';

export const useIntegrations = (companyId = '', page = 1, limit = 10) => {
    return useQuery({
        queryKey: ['integrations', companyId, page, limit],
        queryFn: () => marketIntegrationService.fetchTheIntegrations({ companyId, page, limit }),
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
    });
};

export const useVerifyIntegration = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ companyId, endpointId, apiKey }: { companyId: string; endpointId: string; apiKey: string }) =>
            marketIntegrationService.verifyIntegrationApiKey(companyId, endpointId, apiKey),

        onSuccess: (data, variables) => {
            if (data?.resultObject?.isSuccess) {
                Swal.fire({
                    title: 'API Key verified successfully!',
                    text:
                        data.message ||
                        'Your candidates data will now be shared with OfferX to track their offer status.',
                    icon: SuccessIcon,
                    confirmButtonText: 'OK',
                });

                queryClient.invalidateQueries({
                    queryKey: ['integrations', variables.companyId],
                });
            } else {
                Swal.fire({
                    title: data.message || 'Invalid API Key',
                    icon: ErrorIcon,
                    confirmButtonText: 'OK',
                });
            }
        },

        onError: (error: any) => {
            // Swal.fire({
            //     title: 'Something went wrong',
            //     icon: ErrorIcon,
            //     confirmButtonText: 'OK',
            // });
        },
    });
};

export const useSubmitIntegration = () => {
    return useMutation({
        mutationFn: async (data: any) => {
            return await emailService.create(data);
        },
        onSuccess: () => {},
        onError: () => {},
    });
};

export const useCandidateEmail = (marketPlaceIdValue: string) => {
    return useQuery({
        queryKey: ['candidateEmail', marketPlaceIdValue],
        queryFn: async () => {
            const { resultObject } = await claimedCandidateExternalService.getMarketPlaceUnClaimedCandiateEmail(
                marketPlaceIdValue
            );

            if (resultObject?.hash === 'NA') {
                return { isAlreadyRegistered: true };
            }

            if (resultObject?.hash && resultObject.hash !== 'NA') {
                const decryptedHashedData = decryptData(resultObject?.hash, true);
                const email = JSON.parse(decryptedHashedData).Email;
                localStorageSetItem('Email', email);
                return { ...JSON.parse(decryptedHashedData), isAlreadyRegistered: false };
            }

            return null;
        },
        refetchOnWindowFocus: false,
        enabled: !!marketPlaceIdValue,
        // staleTime: 1000 * 60 * 5,
    });
};
